import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ErrorState.module.css";

/** How long the panel waits before it tries again on its own. */
export const RetryAfterSeconds = 10;

/**
 * The shared "something went wrong" state, with an optional retry action. `QR-07` requires the
 * portal to stay usable when something it depends on fails — this is the honest failure state
 * every screen should reach for instead of leaving a blank panel or throwing.
 *
 * Trying again is the only thing a person can do here, so with a retry action the panel also tries
 * again on its own every {@link RetryAfterSeconds} seconds, counting down in words and with a bar
 * along its bottom edge, as the warning notice does. A page left open on an unattended screen then
 * recovers by itself after a short outage. Unlike the warning notice, pointing at it does not pause
 * the count: a pointer left resting on an unattended screen must not stop it recovering.
 *
 * @param props.title - Overrides the default heading.
 * @param props.description - Overrides the default explanatory text.
 * @param props.onRetry - When provided, renders a "Try again now" button that calls it, and calls it
 *   on its own when the countdown ends.
 */
export function ErrorState({
  title,
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const canRetry = onRetry !== undefined;
  const [remaining, setRemaining] = useState(RetryAfterSeconds);
  // Restarts the countdown bar when a retry leaves this panel in place (most pages replace it with the loading state).
  const [cycle, setCycle] = useState(0);
  const deadline = useRef(0);
  const retry = useEffectEvent(() => onRetry?.());

  useEffect(() => {
    if (!canRetry) {
      return undefined;
    }

    deadline.current = Date.now() + RetryAfterSeconds * 1000;
    const timer = window.setInterval(() => {
      const left = Math.ceil((deadline.current - Date.now()) / 1000);
      if (left > 0) {
        setRemaining(left);
        return;
      }

      deadline.current = Date.now() + RetryAfterSeconds * 1000;
      setRemaining(RetryAfterSeconds);
      setCycle((current) => current + 1);
      retry();
    }, 250);
    return () => window.clearInterval(timer);
  }, [canRetry]);

  return (
    <div className={styles.panel} role="alert">
      <span className={styles.icon} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 7.5v5.5M12 16.5h.01" />
        </svg>
      </span>
      <div className={styles.body}>
        <h2 className={styles.title}>{title ?? t("states.error.defaultTitle")}</h2>
        <p className={styles.description}>{description ?? t("states.error.defaultDescription")}</p>
        {canRetry && <p className="visually-hidden">{t("states.error.autoRetry", { seconds: RetryAfterSeconds })}</p>}
        {canRetry && (
          <div className={styles.actions}>
            <button type="button" className="button button-secondary" onClick={onRetry}>
              {t("states.error.retryNow")}
            </button>
            {/* The alert says once that it retries; the ticking count is for the eye only, so it is not announced every second. */}
            <span className={styles.countdownText} aria-hidden="true">
              {t("states.error.retryingIn", { count: remaining })}
            </span>
          </div>
        )}
      </div>
      {canRetry && (
        <span
          key={cycle}
          className={styles.countdown}
          style={{ animationDuration: `${String(RetryAfterSeconds)}s` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
